import { useId, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@ds/core';
import { Alert } from '@ds/feedback';
import { FormField, Input } from '@ds/forms';
import { criarConta } from '@/session/sessao';

/**
 * Criar conta é **aceitar convite**, não abrir clínica — enquanto o autocadastro
 * (0150) estiver desligado (`VITE_AUTOCADASTRO`).
 *
 * O tenant nasce pela plataforma; aqui só se define a senha de quem a recepção
 * já cadastrou como membro. O vínculo é feito pelo e-mail, num gatilho do banco
 * (migração `0007`) — e é por isso que a tela avisa antes: conta criada com
 * e-mail que ninguém cadastrou existe e não entra em lugar nenhum.
 */
export function AceitarConvite() {
    const navigate = useNavigate();
    const [nome, setNome] = useState('');
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [confirmacao, setConfirmacao] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [pendente, setPendente] = useState(false);
    const [criando, setCriando] = useState(false);

    const idNome = useId();
    const idEmail = useId();
    const idSenha = useId();
    const idConfirmacao = useId();

    const enviar = async () => {
        setErro(null);
        /* A conferência é aqui porque o servidor não a faz: ele guarda a senha
           que receber, inclusive a que foi digitada errado duas vezes. */
        if (senha.length < 8) {
            setErro('A senha precisa de pelo menos 8 caracteres.');
            return;
        }
        if (senha !== confirmacao) {
            setErro('As duas senhas não são iguais.');
            return;
        }
        setCriando(true);
        const r = await criarConta({ nome, email, senha });
        setCriando(false);
        if (!r.ok) {
            setErro(r.erro);
            return;
        }
        if (r.precisaConfirmar) {
            setPendente(true);
            return;
        }
        navigate('/entrar', { replace: true });
    };

    if (pendente) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>
                    Confirme o e-mail
                </h1>
                <Alert tone="success" dismissible={false} title="Conta criada">
                    Enviamos um link para <strong>{email}</strong>. A conta só entra depois que ele for aberto.
                </Alert>
                <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                    Se este e-mail ainda não tiver sido cadastrado por uma clínica, a conta vai existir sem
                    acesso a nenhuma — quem cadastra é a recepção, em Configurações › Equipe.
                </p>
                <Button onClick={() => navigate('/entrar')} fullWidth>Ir para a entrada</Button>
            </div>
        );
    }

    return (
        <form
            onSubmit={(e) => { e.preventDefault(); void enviar(); }}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
            <div>
                <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>Criar conta</h1>
                <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                    Use o <strong>mesmo e-mail</strong> que a clínica cadastrou. É por ele que a conta encontra a
                    equipe — criar clínica nova não se faz por aqui.
                </p>
            </div>

            {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

            <FormField label="Nome" htmlFor={idNome} required>
                <Input id={idNome} name="nome" autoComplete="name" value={nome} onChange={setNome} placeholder="Como aparece na agenda" />
            </FormField>

            <FormField label="E-mail" htmlFor={idEmail} required>
                <Input id={idEmail} type="email" name="email" autoComplete="username" value={email} onChange={setEmail} placeholder="voce@clinicavecchia.com.br" />
            </FormField>

            <FormField label="Senha" htmlFor={idSenha} hint="Pelo menos 8 caracteres." required>
                <Input id={idSenha} type="password" name="senha" autoComplete="new-password" value={senha} onChange={setSenha} />
            </FormField>

            <FormField label="Repita a senha" htmlFor={idConfirmacao} required>
                <Input id={idConfirmacao} type="password" name="confirmacao" autoComplete="new-password" value={confirmacao} onChange={setConfirmacao} />
            </FormField>

            <Button type="submit" fullWidth loading={criando}>Criar conta</Button>

            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', textAlign: 'center' }}>
                Já tem conta? <Link to="/entrar" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>Entrar</Link>
            </p>
        </form>
    );
}
